import React from 'react'

import MainLayout from 'app/components/structure/Layout/MainLayout'
import { observer } from 'mobx-react-lite'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Form, SizableText, YStack } from '@mythical/ui'
import { Button } from 'app/components/primitive/Button'
import { InputRhf } from 'app/components/primitive/Form'
import { Container } from 'app/components/structure/Container'

const formSchema = z.object({
  externalId: z.string(),
})

export type MockLoginFormValues = z.infer<typeof formSchema>

export const MockLoginScreen = observer(() => {
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)
  const { control, handleSubmit } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      externalId: '',
    },
  })

  return (
    <MainLayout noSidebar noScroll noSearch>
      <Container paddedX>
        <Form
          gap="$3.5"
          onSubmit={handleSubmit(async (data) => {
            setErrorMessage(null)

            const response = await fetch('/api/auth/mock-login', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ externalId: data.externalId }),
            })

            if (!response.ok) {
              const responseText = await response.text()
              let body: { details?: string; error?: string } = {}

              try {
                body = JSON.parse(responseText)
              } catch {
                body = { error: responseText }
              }

              setErrorMessage(
                body?.details || body?.error || 'Mock login failed'
              )
              return
            }

            window.location.pathname = '/'
          })}
        >
          <YStack jc="center" gap="$3.5" ai="center">
            <SizableText ff="$heading" size="$l" textTransform="uppercase">
              Mock Login
            </SizableText>
            <InputRhf
              theme="form"
              id="externalId"
              name="externalId"
              label="External ID"
              placeholder="External ID"
              fullWidth
              control={control}
              required
            />
            <Form.Trigger asChild fd="row" ai="center">
              <Button>Login</Button>
            </Form.Trigger>
            {errorMessage ? (
              <SizableText color="$red10">{errorMessage}</SizableText>
            ) : null}
          </YStack>
        </Form>
      </Container>
    </MainLayout>
  )
})
